/**
 * Shared Device Breakpoint Configuration
 *
 * This module provides a centralized source of truth for device viewport dimensions
 * and breakpoint ranges used across the Kassie Child App.
 *
 * Used by:
 * - Library grid card responsive design (content-cards.css)
 * - Story page layouts (pageFlip.js)
 * - Screen debugger (screenDebugger.js)
 * - Responsive reporting tool
 *
 * @module deviceBreakpoints
 */

/**
 * Device viewport dimensions for all supported devices
 * Each device includes portrait and landscape viewport configurations
 */
export const DEVICE_VIEWPORTS = {
  // Phones
  'iphone-se': {
    portrait: { width: 375, height: 667 },
    landscape: { width: 667, height: 375 }
  },
  'iphone-xr': {
    portrait: { width: 414, height: 896 },
    landscape: { width: 896, height: 414 }
  },
  'iphone-12-pro': {
    portrait: { width: 390, height: 844 },
    landscape: { width: 844, height: 390 }
  },
  'iphone-14-pro-max': {
    portrait: { width: 430, height: 932 },
    landscape: { width: 932, height: 430 }
  },
  'pixel-7': {
    portrait: { width: 412, height: 915 },
    landscape: { width: 915, height: 412 }
  },
  'galaxy-s8-plus': {
    portrait: { width: 360, height: 740 },
    landscape: { width: 740, height: 360 }
  },
  'galaxy-s20-ultra': {
    portrait: { width: 412, height: 915 },
    landscape: { width: 915, height: 412 }
  },
  'galaxy-a51-71': {
    portrait: { width: 412, height: 914 },
    landscape: { width: 914, height: 412 }
  },

  // Tablets
  'ipad-mini': {
    portrait: { width: 768, height: 1024 },
    landscape: { width: 1024, height: 768 }
  },
  'ipad-air': {
    portrait: { width: 820, height: 1180 },
    landscape: { width: 1180, height: 820 }
  },
  'ipad-pro': {
    portrait: { width: 1024, height: 1366 },
    landscape: { width: 1366, height: 1024 }
  },
  'surface-pro-7': {
    portrait: { width: 912, height: 1368 },
    landscape: { width: 1368, height: 912 }
  },

  // Foldables
  'surface-duo-single': {
    portrait: { width: 540, height: 720 },
    landscape: { width: 720, height: 540 }
  },
  'surface-duo-dual': {
    portrait: { width: 1114, height: 720 },
    landscape: { width: 720, height: 1114 }
  },
  'galaxy-z-fold-5-folded': {
    portrait: { width: 344, height: 882 },
    landscape: { width: 882, height: 344 }
  },
  'galaxy-z-fold-5-unfolded': {
    portrait: { width: 748, height: 1812 },
    landscape: { width: 1812, height: 748 }
  },
  'asus-zenbook-fold': {
    portrait: { width: 1280, height: 1920 },
    landscape: { width: 1920, height: 1280 }
  },

  // Smart Displays
  'nest-hub': {
    portrait: { width: 1024, height: 600 },
    landscape: { width: 1024, height: 600 }
  },
  'nest-hub-max': {
    portrait: { width: 1280, height: 800 },
    landscape: { width: 1280, height: 800 }
  }
};

/**
 * Breakpoint ranges for CSS media queries
 * Used primarily for library grid cards and UI components
 * Ordered from smallest to largest to prevent cascade conflicts
 */
export const BREAKPOINT_RANGES = {
  'galaxy-z-fold-5-cover': {
    name: 'Galaxy Z Fold 5 Cover Portrait',
    minWidth: 340,
    maxWidth: 350,
    orientation: 'portrait'
  },
  'small-mobile': {
    name: 'Small Mobile Portrait',
    minWidth: 360,
    maxWidth: 374,
    orientation: 'portrait'
  },
  'galaxy-s8-plus': {
    name: 'Samsung Galaxy S8+ Portrait',
    minWidth: 360,
    maxWidth: 370,
    minHeight: 720,
    maxHeight: 760,
    orientation: 'portrait'
  },
  'iphone-se': {
    name: 'iPhone SE Portrait',
    minWidth: 375,
    maxWidth: 389,
    orientation: 'portrait'
  },
  'iphone-12-pro-xr': {
    name: 'iPhone 12 Pro/XR Portrait',
    minWidth: 390,
    maxWidth: 411,
    orientation: 'portrait'
  },
  'pixel-7': {
    name: 'Pixel 7 Portrait',
    minWidth: 411,
    maxWidth: 413,
    orientation: 'portrait'
  },
  'iphone-xr-upper': {
    name: 'iPhone XR Upper Range Portrait',
    minWidth: 413,
    maxWidth: 430,
    orientation: 'portrait'
  },
  'iphone-14-pro-max': {
    name: 'iPhone 14 Pro Max Portrait',
    minWidth: 428,
    maxWidth: 432,
    orientation: 'portrait'
  },
  'larger-mobile': {
    name: 'Larger Mobile Portrait',
    minWidth: 430,
    maxWidth: 767,
    orientation: 'portrait'
  },
  'tablet': {
    name: 'Tablet',
    minWidth: 768,
    maxWidth: 1023
  },
  'desktop': {
    name: 'Desktop',
    minWidth: 1024
  }
};

/**
 * StPageFlip display modes by device and orientation
 * Determines how story pages are rendered
 */
export const PAGEFLIP_MODES = {
  'iphone-se': { portrait: 'single', landscape: 'single-rotated' },
  'iphone-xr': { portrait: 'single', landscape: 'single-rotated' },
  'iphone-12-pro': { portrait: 'single', landscape: 'single-rotated' },
  'iphone-14-pro-max': { portrait: 'single', landscape: 'single-rotated' },
  'pixel-7': { portrait: 'single', landscape: 'single-rotated' },
  'galaxy-s8-plus': { portrait: 'single', landscape: 'single-rotated' },
  'galaxy-s20-ultra': { portrait: 'single', landscape: 'single-rotated' },
  'galaxy-a51-71': { portrait: 'single', landscape: 'single-rotated' },
  'ipad-mini': { portrait: 'single', landscape: 'double' },
  'ipad-air': { portrait: 'single', landscape: 'double' },
  'ipad-pro': { portrait: 'single', landscape: 'double' },
  'surface-pro-7': { portrait: 'single', landscape: 'double' },
  'surface-duo-single': { portrait: 'single', landscape: 'single-rotated' },
  'surface-duo-dual': { portrait: 'double', landscape: 'single' },
  'galaxy-z-fold-5-folded': { portrait: 'single', landscape: 'single-rotated' },
  'galaxy-z-fold-5-unfolded': { portrait: 'single', landscape: 'double' },
  'asus-zenbook-fold': { portrait: 'single', landscape: 'double' },
  'nest-hub': { portrait: 'double', landscape: 'double' },
  'nest-hub-max': { portrait: 'double', landscape: 'double' }
};

/**
 * StPageFlip aspect ratio constraints
 *
 * Pages are always portrait-shaped (taller than wide).
 * The outer book container is constrained to maintain proper aspect ratios:
 * - Single page mode: 4:5 aspect ratio (width:height) - one portrait page
 * - Double page mode: 8:5 aspect ratio (width:height) - two portrait pages side-by-side
 *
 * This prevents the book from becoming too wide in landscape orientations,
 * which would cause pages to become landscape-shaped instead of portrait.
 */
export const PAGEFLIP_ASPECT_RATIOS = {
  single: { width: 1, height: 1.4 },   // Single portrait page: 1:1.4 (playing card)
  double: { width: 2, height: 1.4 }    // Two portrait pages side-by-side: 2:1.4
};

/**
 * Get device configuration by ID
 * @param {string} deviceId - Device identifier
 * @returns {Object|null} Device configuration or null if not found
 */
export function getDeviceConfig(deviceId) {
  return DEVICE_VIEWPORTS[deviceId] || null;
}

/**
 * Get all device IDs
 * @returns {string[]} Array of device identifiers
 */
export function getAllDeviceIds() {
  return Object.keys(DEVICE_VIEWPORTS);
}

/**
 * Get breakpoint range by ID
 * @param {string} breakpointId - Breakpoint identifier
 * @returns {Object|null} Breakpoint configuration or null if not found
 */
export function getBreakpointRange(breakpointId) {
  return BREAKPOINT_RANGES[breakpointId] || null;
}

/**
 * Get all breakpoint IDs in order (smallest to largest)
 * @returns {string[]} Array of breakpoint identifiers
 */
export function getAllBreakpointIds() {
  return Object.keys(BREAKPOINT_RANGES);
}

/**
 * Find matching breakpoint for given dimensions
 * @param {number} width - Viewport width
 * @param {number} height - Viewport height
 * @param {string} orientation - 'portrait' or 'landscape'
 * @returns {string|null} Matching breakpoint ID or null
 */
export function findMatchingBreakpoint(width, height, orientation) {
  for (const [id, range] of Object.entries(BREAKPOINT_RANGES)) {
    // Check orientation if specified
    if (range.orientation && range.orientation !== orientation) {
      continue;
    }

    // Check width range
    if (width < range.minWidth || (range.maxWidth && width > range.maxWidth)) {
      continue;
    }

    // Check height range if specified
    if (range.minHeight && height < range.minHeight) {
      continue;
    }
    if (range.maxHeight && height > range.maxHeight) {
      continue;
    }

    return id;
  }

  return null;
}

/**
 * Get StPageFlip mode for device and orientation
 * @param {string} deviceId - Device identifier
 * @param {string} orientation - 'portrait' or 'landscape'
 * @returns {string|null} PageFlip mode or null if not found
 */
export function getPageFlipMode(deviceId, orientation) {
  const modes = PAGEFLIP_MODES[deviceId];
  return modes ? modes[orientation] : null;
}

/**
 * Get aspect ratio for a given PageFlip mode
 * @param {string} mode - PageFlip mode ('single', 'double', or 'single-rotated')
 * @returns {Object} { width, height, ratio } - Aspect ratio dimensions and decimal ratio
 */
export function getPageFlipAspectRatio(mode) {
  // single-rotated uses same aspect ratio as single
  const normalizedMode = mode === 'single-rotated' ? 'single' : mode;
  const aspectRatio = PAGEFLIP_ASPECT_RATIOS[normalizedMode] || PAGEFLIP_ASPECT_RATIOS.single;

  return {
    width: aspectRatio.width,
    height: aspectRatio.height,
    ratio: aspectRatio.width / aspectRatio.height
  };
}

/**
 * Calculate constrained dimensions for StPageFlip container
 * - Single page modes: Use 100% of available space
 * - Double page mode: Constrain to 8:5 aspect ratio maximum
 *
 * @param {number} availableWidth - Available container width
 * @param {number} availableHeight - Available container height
 * @param {string} mode - PageFlip mode ('single', 'double', or 'single-rotated')
 * @returns {Object} { width, height, pageWidth, pageHeight } - Constrained dimensions
 */
export function calculateConstrainedPageFlipDimensions(availableWidth, availableHeight, mode) {
  let bookWidth, bookHeight;

  if (mode === 'double') {
    // Double page mode: Apply 2:1.4 aspect ratio constraint (two playing cards side-by-side)
    const targetRatio = 2 / 1.4; // ~1.4286
    const currentRatio = availableWidth / availableHeight;

    if (currentRatio > targetRatio) {
      bookHeight = availableHeight;
      bookWidth = bookHeight * targetRatio;
    } else {
      bookWidth = availableWidth;
      bookHeight = bookWidth / targetRatio;
    }
  } else {
    // Single page mode: Constrain to 1:1.4 portrait aspect ratio (playing card)
    const targetRatio = 1 / 1.4; // ~0.7143 (width:height)
    const currentRatio = availableWidth / availableHeight;

    if (currentRatio > targetRatio) {
      // Screen is wider than 1:1.4 — constrain by height, apply portrait ratio
      bookHeight = availableHeight;
      bookWidth = bookHeight * targetRatio;
    } else {
      // Screen is narrower/taller than 1:1.4 — use full width, height follows ratio
      bookWidth = availableWidth;
      bookHeight = bookWidth / targetRatio;
      // Don't exceed available height
      if (bookHeight > availableHeight) {
        bookHeight = availableHeight;
        bookWidth = bookHeight * targetRatio;
      }
    }
  }

  // Calculate individual page dimensions
  const pageWidth = mode === 'double' ? bookWidth / 2 : bookWidth;
  const pageHeight = bookHeight;

  // Debug logging
  console.log(`📐 Dimension calculation:`, {
    mode,
    available: { width: availableWidth, height: availableHeight },
    constraint: mode === 'double' ? '2:1.4' : '1:1.4',
    result: { bookWidth, bookHeight, pageWidth, pageHeight },
    pageAspectRatio: (pageWidth / pageHeight).toFixed(3)
  });

  return {
    width: Math.floor(bookWidth),
    height: Math.floor(bookHeight),
    pageWidth: Math.floor(pageWidth),
    pageHeight: Math.floor(pageHeight)
  };
}

/**
 * Story Text Sizing Configuration
 *
 * Defines text sizing rules for story pages across all supported devices.
 * Each device includes:
 * - viewport: Portrait and landscape dimensions
 * - modes: StPageFlip display modes (single, double, single-rotated)
 * - textSizing: Font size rules for small, medium, and large text
 *
 * Text sizing uses container query units (cqi) that scale proportionally
 * to the page container width. Fallback pixel values are provided for
 * browsers that don't support container queries.
 *
 * Formula: font-size = (width * cqi_coefficient) + rem_offset
 * - Small: 3cqi + 0.5rem (10-24px range)
 * - Medium: 4cqi + 1rem (14-32px range)
 * - Large: 5cqi + 1rem (18-40px range)
 *
 * @see docs/RESPONSIVE_DESIGN_SYSTEM.md
 */
export const STORY_TEXT_SIZING_CONFIG = {
  // Phones
  'iphone-se': {
    viewport: { portrait: { width: 375, height: 667 }, landscape: { width: 667, height: 375 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 11.25, landscape: 11.25 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 15, landscape: 15 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 18.75, landscape: 18.75 } }
    }
  },
  'iphone-xr': {
    viewport: { portrait: { width: 414, height: 896 }, landscape: { width: 896, height: 414 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 12.42, landscape: 12.42 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 16.56, landscape: 16.56 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 20.7, landscape: 20.7 } }
    }
  },
  'iphone-12-pro': {
    viewport: { portrait: { width: 390, height: 844 }, landscape: { width: 844, height: 390 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 11.7, landscape: 11.7 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 15.6, landscape: 15.6 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 19.5, landscape: 19.5 } }
    }
  },
  'iphone-14-pro-max': {
    viewport: { portrait: { width: 430, height: 932 }, landscape: { width: 932, height: 430 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 12.9, landscape: 12.9 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 17.2, landscape: 17.2 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 21.5, landscape: 21.5 } }
    }
  },
  'pixel-7': {
    viewport: { portrait: { width: 412, height: 915 }, landscape: { width: 915, height: 412 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 12.36, landscape: 12.36 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 16.48, landscape: 16.48 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 20.6, landscape: 20.6 } }
    }
  },
  'galaxy-s8-plus': {
    viewport: { portrait: { width: 360, height: 740 }, landscape: { width: 740, height: 360 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 10.8, landscape: 10.8 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 14.4, landscape: 14.4 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 18, landscape: 18 } }
    }
  },
  'galaxy-s20-ultra': {
    viewport: { portrait: { width: 412, height: 915 }, landscape: { width: 915, height: 412 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 12.36, landscape: 12.36 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 16.48, landscape: 16.48 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 20.6, landscape: 20.6 } }
    }
  },
  'galaxy-a51-71': {
    viewport: { portrait: { width: 412, height: 914 }, landscape: { width: 914, height: 412 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 12.36, landscape: 12.36 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 16.48, landscape: 16.48 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 20.6, landscape: 20.6 } }
    }
  },

  // Tablets
  'ipad-mini': {
    viewport: { portrait: { width: 768, height: 1024 }, landscape: { width: 1024, height: 768 } },
    modes: { portrait: 'single', landscape: 'double' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 23.04, landscape: 15.36 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 30.72, landscape: 20.48 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 38.4, landscape: 25.6 } }
    }
  },
  'ipad-air': {
    viewport: { portrait: { width: 820, height: 1180 }, landscape: { width: 1180, height: 820 } },
    modes: { portrait: 'single', landscape: 'double' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 24.6, landscape: 17.7 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 32.8, landscape: 23.6 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 41, landscape: 29.5 } }
    }
  },
  'ipad-pro': {
    viewport: { portrait: { width: 1024, height: 1366 }, landscape: { width: 1366, height: 1024 } },
    modes: { portrait: 'single', landscape: 'double' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 30.72, landscape: 20.49 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 40.96, landscape: 27.32 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 51.2, landscape: 34.15 } }
    }
  },
  'surface-pro-7': {
    viewport: { portrait: { width: 912, height: 1368 }, landscape: { width: 1368, height: 912 } },
    modes: { portrait: 'single', landscape: 'double' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 27.36, landscape: 20.52 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 36.48, landscape: 27.36 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 45.6, landscape: 34.2 } }
    }
  },

  // Foldables
  'surface-duo-single': {
    viewport: { portrait: { width: 540, height: 720 }, landscape: { width: 720, height: 540 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 16.2, landscape: 16.2 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 21.6, landscape: 21.6 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 27, landscape: 27 } }
    }
  },
  'surface-duo-dual': {
    viewport: { portrait: { width: 1114, height: 720 }, landscape: { width: 720, height: 1114 } },
    modes: { portrait: 'double', landscape: 'single' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 16.71, landscape: 21.6 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 22.28, landscape: 28.8 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 27.85, landscape: 36 } }
    }
  },
  'galaxy-z-fold-5-folded': {
    viewport: { portrait: { width: 344, height: 882 }, landscape: { width: 882, height: 344 } },
    modes: { portrait: 'single', landscape: 'single-rotated' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 10.32, landscape: 10.32 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 13.76, landscape: 13.76 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 17.2, landscape: 17.2 } }
    }
  },
  'galaxy-z-fold-5-unfolded': {
    viewport: { portrait: { width: 748, height: 1812 }, landscape: { width: 1812, height: 748 } },
    modes: { portrait: 'single', landscape: 'double' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 22.44, landscape: 27.18 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 29.92, landscape: 36.24 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 37.4, landscape: 45.3 } }
    }
  },
  'asus-zenbook-fold': {
    viewport: { portrait: { width: 1280, height: 1920 }, landscape: { width: 1920, height: 1280 } },
    modes: { portrait: 'single', landscape: 'double' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 38.4, landscape: 28.8 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 51.2, landscape: 38.4 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 64, landscape: 48 } }
    }
  },

  // Smart Displays
  'nest-hub': {
    viewport: { portrait: { width: 1024, height: 600 }, landscape: { width: 1024, height: 600 } },
    modes: { portrait: 'double', landscape: 'double' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 15.36, landscape: 15.36 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 20.48, landscape: 20.48 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 25.6, landscape: 25.6 } }
    }
  },
  'nest-hub-max': {
    viewport: { portrait: { width: 1280, height: 800 }, landscape: { width: 1280, height: 800 } },
    modes: { portrait: 'double', landscape: 'double' },
    textSizing: {
      small: { portrait: '3cqi + 0.5rem', landscape: '3cqi + 0.5rem', fallback: { portrait: 19.2, landscape: 19.2 } },
      medium: { portrait: '4cqi + 1rem', landscape: '4cqi + 1rem', fallback: { portrait: 25.6, landscape: 25.6 } },
      large: { portrait: '5cqi + 1rem', landscape: '5cqi + 1rem', fallback: { portrait: 32, landscape: 32 } }
    }
  }
};

/**
 * Get text sizing configuration for a device
 * @param {string} deviceId - Device identifier
 * @returns {Object|null} Text sizing configuration or null if not found
 */
export function getTextSizingConfig(deviceId) {
  return STORY_TEXT_SIZING_CONFIG[deviceId] || null;
}

/**
 * Get text size value for device, orientation, and size
 * @param {string} deviceId - Device identifier
 * @param {string} orientation - 'portrait' or 'landscape'
 * @param {string} size - 'small', 'medium', or 'large'
 * @param {boolean} useContainerQueries - Whether browser supports container queries
 * @returns {string|null} Font size value or null if not found
 */
export function getTextSizeValue(deviceId, orientation, size, useContainerQueries = true) {
  const config = STORY_TEXT_SIZING_CONFIG[deviceId];
  if (!config || !config.textSizing[size]) {
    return null;
  }

  const sizeConfig = config.textSizing[size];

  if (useContainerQueries) {
    return sizeConfig[orientation];
  } else {
    return `${sizeConfig.fallback[orientation]}px`;
  }
}

/**
 * Content Card Aspect Ratio Constraints
 *
 * Content cards in the library grid are constrained to maintain
 * readable proportions across all devices:
 * - Tallest: 9:16 aspect ratio (0.5625) - prevents cards from being too narrow
 * - Widest: 4:5 aspect ratio (0.8) - prevents cards from being too wide
 *
 * These constraints ensure cards remain visually balanced and content
 * (images, text, buttons) stays properly proportioned.
 */
export const CONTENT_CARD_ASPECT_RATIOS = {
  min: { width: 9, height: 16 },    // Tallest: 9:16 (0.5625)
  max: { width: 4, height: 5 }      // Widest: 4:5 (0.8)
};

/**
 * Calculate constrained dimensions for content cards
 * Ensures cards maintain readable aspect ratios (not too tall, not too wide)
 *
 * @param {number} availableWidth - Available container width
 * @param {number} availableHeight - Available container height
 * @returns {Object} { width, height } - Constrained card dimensions
 */
export function calculateConstrainedCardDimensions(availableWidth, availableHeight) {
  const minRatio = CONTENT_CARD_ASPECT_RATIOS.min.width / CONTENT_CARD_ASPECT_RATIOS.min.height;
  const maxRatio = CONTENT_CARD_ASPECT_RATIOS.max.width / CONTENT_CARD_ASPECT_RATIOS.max.height;
  const currentRatio = availableWidth / availableHeight;

  let cardWidth, cardHeight;

  if (currentRatio < minRatio) {
    // Too tall - constrain by height, adjust width
    cardHeight = availableHeight;
    cardWidth = cardHeight * minRatio;
  } else if (currentRatio > maxRatio) {
    // Too wide - constrain by width, adjust height
    cardWidth = availableWidth;
    cardHeight = cardWidth / maxRatio;
  } else {
    // Within acceptable range - use available space
    cardWidth = availableWidth;
    cardHeight = availableHeight;
  }

  return {
    width: Math.floor(cardWidth),
    height: Math.floor(cardHeight)
  };
}
